/**
 * Calendar view - month grid with event dots, day detail, month navigation, swipe.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { CreateEventInput, CreateTaskInput } from '@anna-calendar/shared';
import { useEventsStore } from '../store/events';
import { useTasksStore } from '../store/tasks';
import { useNotesStore } from '../store/notes';
import { useSwipeGesture } from '../hooks/useSwipe';
import { EventModal } from '../components/EventModal';
import { TaskModal } from '../components/TaskModal';
import { NoteModal } from '../components/NoteModal';
import {
  formatDateISO, todayISO, addMonths,
  daysInMonth, firstDayOfWeek as getFirstDow, formatDateTime, formatTime,
  priorityLabel, priorityColor,
} from '../utils/dates';
import { expandEvents } from '../utils/recurrence';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function CalendarView() {
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);

  const { events, fetchEvents, createEvent } = useEventsStore();
  const { tasks, fetchTasks, createTask, updateTask } = useTasksStore();
  const { notes, fetchNotes, createNote } = useNotesStore();

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  // Fetch data for the visible month only (lazy loading)
  const loadMonth = useCallback(async () => {
    const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const endYear = month + 2 > 12 ? year + 1 : year;
    const endFormatted = `${endYear}-${String(month + 2 > 12 ? 1 : month + 2).padStart(2, '0')}-01`;

    const end = new Date(year, month + 1, 1).toISOString();
    // Fetch events with a wide lookback to capture recurring events whose
    // original start_time predates this month but whose RRULE generates
    // occurrences within it.
    const lookback = new Date(year - 1, month, 1).toISOString();
    await fetchEvents({ start: lookback, end });
    // Fetch only tasks due in the visible month (lazy loading)
    await fetchTasks({ due_date_start: startDate, due_date_end: endFormatted });
    // Fetch notes for the visible month
    await fetchNotes();
  }, [year, month, fetchEvents, fetchTasks, fetchNotes]);

  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  // Swipe between months
  const swipeHandlers = useSwipeGesture({
    onSwipeLeft: () => setViewDate((d) => addMonths(d, 1)),
    onSwipeRight: () => setViewDate((d) => addMonths(d, -1)),
    threshold: 50,
  });

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const firstDow = getFirstDow(viewDate);
    const numDays = daysInMonth(viewDate);
    const days: (number | null)[] = [];

    // Leading blanks
    for (let i = 0; i < firstDow; i++) days.push(null);
    // Day numbers
    for (let d = 1; d <= numDays; d++) days.push(d);
    // Trailing blanks to fill last row
    while (days.length % 7 !== 0) days.push(null);

    return days;
  }, [viewDate]);

  // Expand recurring events into visible occurrences for the current month.
  const monthRangeStart = new Date(year, month, 1).toISOString();
  const monthRangeEnd = new Date(year, month + 1, 1).toISOString();

  const expandedEvents = useMemo(
    () => expandEvents(events, monthRangeStart, monthRangeEnd),
    [events, monthRangeStart, monthRangeEnd],
  );

  // Group events by date for dot indicators (uses expanded occurrences)
  const eventsByDate = useMemo(() => {
    const map = new Map<string, typeof expandedEvents>();
    for (const e of expandedEvents) {
      const d = formatDateISO(new Date(e.start_time));
      if (!map.has(d)) map.set(d, []);
      map.get(d)!.push(e);
    }
    return map;
  }, [expandedEvents]);

  // Group notes by date
  const notesByDate = useMemo(() => {
    const map = new Map<string, typeof notes>();
    for (const n of notes) {
      if (!map.has(n.date)) map.set(n.date, []);
      map.get(n.date)!.push(n);
    }
    return map;
  }, [notes]);

  // Selected date detail (uses expanded occurrences)
  const selectedEvents = selectedDate
    ? expandedEvents.filter((e) => formatDateISO(new Date(e.start_time)) === selectedDate).sort((a, b) => a.start_time.localeCompare(b.start_time))
    : [];
  const selectedTasks = selectedDate
    ? tasks.filter((t) => t.due_date === selectedDate).sort((a, b) => b.priority - a.priority)
    : [];
  const selectedNotes = selectedDate
    ? notes.filter((n) => n.date === selectedDate)
    : [];

  const monthLabel = viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const today = todayISO();

  const handleCreateEvent = async (data: CreateEventInput) => {
    await createEvent(data);
    loadMonth();
  };

  const handleCreateTask = async (data: CreateTaskInput) => {
    await createTask(data);
    loadMonth();
  };

  const handleCreateNote = async (data: { content: string; date: string; color: string }) => {
    await createNote(data);
    loadMonth();
  };

  const handleToggleTask = async (id: string, completed: number) => {
    await updateTask(id, { completed: completed ? 0 : 1 });
  };

  return (
    <div className="h-full flex desktop-flex-row overflow-hidden">
      {/* Calendar grid */}
      <div className="flex-1 overflow-y-auto p-4 desktop-pb" {...swipeHandlers}>
        {/* Month header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setViewDate((d) => addMonths(d, -1))}
            className="p-2 rounded-lg"
            style={{ minHeight: '44px', minWidth: '44px', color: 'var(--text-secondary)' }}
            aria-label="Previous month"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {monthLabel}
            </h2>
            {formatDateISO(viewDate) !== today &&
              formatDateISO(new Date(year, month, 1)) !==
                formatDateISO(new Date(new Date().getFullYear(), new Date().getMonth(), 1)) && (
                <button
                  onClick={() => setViewDate(new Date())}
                  className="px-2 py-1 rounded text-xs font-medium"
                  style={{ backgroundColor: 'var(--accent-soft)', color: 'var(--accent)' }}
                >
                  Today
                </button>
              )}
          </div>
          <button
            onClick={() => setViewDate((d) => addMonths(d, 1))}
            className="p-2 rounded-lg"
            style={{ minHeight: '44px', minWidth: '44px', color: 'var(--text-secondary)' }}
            aria-label="Next month"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>

        {/* Day labels */}
        <div className="grid grid-cols-7 mb-1">
          {DAY_LABELS.map((d) => (
            <div
              key={d}
              className="text-center text-xs font-medium py-2"
              style={{ color: 'var(--text-muted)' }}
            >
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, idx) => {
            if (day === null) {
              return <div key={`blank-${idx}`} className="aspect-square" />;
            }
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isCurrentDay = dateStr === today;
            const isSelected = dateStr === selectedDate;
            const dayEvents = eventsByDate.get(dateStr) || [];
            const dayNotes = notesByDate.get(dateStr) || [];

            return (
              <button
                key={dateStr}
                onClick={() => setSelectedDate(dateStr)}
                className="aspect-square p-1 flex flex-col items-center justify-start gap-0.5 relative transition-colors rounded-lg"
                style={{
                  backgroundColor: isSelected
                    ? 'var(--accent-soft)'
                    : isCurrentDay
                    ? 'var(--bg-tertiary)'
                    : 'transparent',
                  minHeight: '44px',
                }}
              >
                <span
                  className={`text-sm font-medium ${isCurrentDay ? 'font-bold' : ''}`}
                  style={{
                    backgroundColor: isCurrentDay ? 'var(--accent)' : 'transparent',
                    borderRadius: isCurrentDay ? '50%' : '0',
                    width: isCurrentDay ? '24px' : 'auto',
                    height: isCurrentDay ? '24px' : 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isCurrentDay ? '#fff' : 'var(--text-primary)',
                  }}
                >
                  {day}
                </span>

                {/* Event dots */}
                {dayEvents.length > 0 && (
                  <div className="flex gap-0.5 flex-wrap justify-center">
                    {dayEvents.slice(0, 3).map((e) => (
                      <div
                        key={e.id}
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: e.color }}
                      />
                    ))}
                    {dayEvents.length > 3 && (
                      <span className="text-[8px]" style={{ color: 'var(--text-muted)' }}>+</span>
                    )}
                  </div>
                )}

                {/* Note indicator */}
                {dayNotes.length > 0 && (
                  <div
                    className="w-1.5 h-1.5 rounded-sm"
                    style={{ backgroundColor: dayNotes[0].color }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Day detail panel */}
      {selectedDate && (
        <div
          className="w-full desktop-w-80 overflow-y-auto p-4"
          style={{
            borderTop: '1px solid var(--border-color)',
            backgroundColor: 'var(--bg-secondary)',
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              })}
            </h3>
            <button
              onClick={() => setSelectedDate(null)}
              className="p-2 rounded-lg"
              style={{ color: 'var(--text-muted)', minHeight: '44px', minWidth: '44px' }}
              aria-label="Close detail"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Quick add buttons */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setShowEventModal(true)}
              className="flex-1 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: 'var(--accent)', color: '#fff', minHeight: '44px' }}
            >
              + Event
            </button>
            <button
              onClick={() => setShowTaskModal(true)}
              className="flex-1 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', minHeight: '44px' }}
            >
              + Task
            </button>
            <button
              onClick={() => setShowNoteModal(true)}
              className="flex-1 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', minHeight: '44px' }}
            >
              + Note
            </button>
          </div>

          {/* Events */}
          {selectedEvents.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Events</h4>
              <div className="space-y-2">
                {selectedEvents.map((e) => (
                  <div
                    key={e.id}
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: 'var(--bg-tertiary)', borderLeft: `3px solid ${e.color}` }}
                  >
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{e.title}</p>
                    {!e.all_day && (
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                        {formatDateTime(e.start_time)}
                        {e.end_time && ` - ${formatDateTime(e.end_time)}`}
                      </p>
                    )}
                    {e.all_day === 1 && (
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>All day</p>
                    )}
                    {e.recurrence_rule && (
                      <span className="text-xs mt-0.5 inline-block" style={{ color: 'var(--accent)' }}>Recurring</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tasks */}
          {selectedTasks.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Tasks</h4>
              <div className="space-y-2">
                {selectedTasks.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                    <button
                      onClick={() => handleToggleTask(t.id, t.completed)}
                      className="w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0"
                      style={{
                        borderColor: t.completed ? 'var(--success)' : 'var(--border-color)',
                        backgroundColor: t.completed ? 'var(--success)' : 'transparent',
                        minHeight: '44px',
                        minWidth: '44px',
                        width: '44px',
                        height: '44px',
                      }}
                    >
                      {t.completed ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : null}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-sm truncate"
                        style={{
                          color: t.completed ? 'var(--text-muted)' : 'var(--text-primary)',
                          textDecoration: t.completed ? 'line-through' : 'none',
                        }}
                      >
                        {t.title}
                      </p>
                      {t.due_time && (
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{formatTime(t.due_time)}</span>
                      )}
                    </div>
                    <span className="text-xs" style={{ color: priorityColor(t.priority) }}>
                      {priorityLabel(t.priority)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {selectedNotes.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Notes</h4>
              <div className="space-y-2">
                {selectedNotes.map((n) => (
                  <div
                    key={n.id}
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: 'var(--bg-tertiary)', borderLeft: `3px solid ${n.color}` }}
                  >
                    <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{n.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {selectedEvents.length === 0 && selectedTasks.length === 0 && selectedNotes.length === 0 && (
            <p className="text-sm text-center py-6" style={{ color: 'var(--text-muted)' }}>
              No items for this day
            </p>
          )}
        </div>
      )}

      {/* Modals */}
      <EventModal
        isOpen={showEventModal}
        onClose={() => setShowEventModal(false)}
        onSave={handleCreateEvent}
        defaultDate={selectedDate || undefined}
      />
      <TaskModal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        onSave={handleCreateTask}
        defaultDate={selectedDate || undefined}
      />
      <NoteModal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        onSave={handleCreateNote}
        defaultDate={selectedDate || undefined}
      />
    </div>
  );
}
